import { normalizeReferralCode } from './referralCode';

/**
 * The code resolved from a v2 referral link, held until sign-up reads it.
 *
 * It has to survive the portal's redirects (`/` and `*` both `<Navigate>` to
 * login with a bare URL) and a reload or back-navigation during the wizard, so
 * it lives in `localStorage`, not React state.
 *
 * **A new key, not `referralId`** (GOTU-7432): that key historically held a
 * legacy Mongo referral id, and an office who visited weeks ago with a legacy
 * link still has one sitting there. Reusing the key would post a Mongo id as a
 * v2 code. `useReferralHandler` clears the legacy key while the rebuild is on.
 *
 * Reads are synchronous so GOTU-7317 can prepopulate the field on first render.
 * Every access is guarded: a storage fault (private mode, blocked storage) must
 * degrade to "no code" and never throw into the render path.
 */
const PENDING_REFERRAL_CODE_KEY = 'referralPendingCode';

export const readPendingReferralCode = (): string | null => {
  try {
    const stored = localStorage.getItem(PENDING_REFERRAL_CODE_KEY);

    return normalizeReferralCode(stored) || null;
  } catch {
    return null;
  }
};

export const setPendingReferralCode = (code: string): void => {
  try {
    localStorage.setItem(PENDING_REFERRAL_CODE_KEY, normalizeReferralCode(code));
  } catch {
    // Best effort: losing the code costs the referral, not the page.
  }
};

export const clearPendingReferralCode = (): void => {
  try {
    localStorage.removeItem(PENDING_REFERRAL_CODE_KEY);
  } catch {
    // Best effort, as above.
  }
};

/**
 * GOTU-9922: the code a sign-up in this browser linked, keyed by the office's
 * email. A signed-in tap on that same code then says "already recorded"
 * instead of Not Eligible. Mirrors mobile's `setRecordedReferral`.
 *
 * Browser-local on purpose (no read endpoint exists yet): another browser,
 * cleared storage or another account falls back to Not Eligible.
 */
const RECORDED_REFERRAL_KEY = 'referralRecorded';

const normalizeEmail = (email: string): string => email.trim().toLowerCase();

interface IRecordedReferral {
  email: string;
  code: string;
}

export const setRecordedReferral = ({ email, code }: IRecordedReferral): void => {
  try {
    localStorage.setItem(
      RECORDED_REFERRAL_KEY,
      JSON.stringify({ email: normalizeEmail(email), code: normalizeReferralCode(code) }),
    );
  } catch {
    // Best effort: without it a re-tap shows Not Eligible, as before.
  }
};

export const isRecordedReferralCode = ({ email, code }: IRecordedReferral): boolean => {
  try {
    const stored = localStorage.getItem(RECORDED_REFERRAL_KEY);
    if (!stored || !email) return false;

    const recorded = JSON.parse(stored) as Partial<IRecordedReferral>;

    return (
      recorded.email === normalizeEmail(email) &&
      !!recorded.code &&
      recorded.code === normalizeReferralCode(code)
    );
  } catch {
    return false;
  }
};
